// src/components/auth/AuthRegister.tsx
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Mail, Lock, User, ArrowRight, Loader2, Check, AlertCircle, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const PASSWORD_REGEX = /^(?!\s)(?!.*\s$)(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;

const registerBaseSchema = z.object({
    name: z.string(),
    email: z.string().email(),
    password: z.string(),
    termsAccepted: z.boolean(),
    privacyAccepted: z.boolean(),
    marketingAccepted: z.boolean().optional(),
});

export type RegisterSchema = z.infer<typeof registerBaseSchema>;

interface AuthRegisterProps {
    loading: boolean;
    onRegister: (data: RegisterSchema) => Promise<void> | void;
}

const AuthRegister: React.FC<AuthRegisterProps> = ({
    loading,
    onRegister
}) => {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    const registerSchema = z.object({
        name: z.string().min(2, t('auth.validation.name_min', "Il nome deve avere almeno 2 caratteri")),

        email: z.string().email(t('auth.validation.email_invalid', "Indirizzo email non valido")),

        password: z.string()
            .regex(PASSWORD_REGEX, t('auth.validation.password_complexity', "Minimo 8 caratteri, maiuscole, minuscole, numeri e simboli")),

        termsAccepted: z.boolean().refine((val) => val === true, {
            message: t('auth.validation.terms_required', "Devi accettare i termini"),
        }),

        privacyAccepted: z.boolean().refine((val) => val === true, {
            message: t('auth.validation.privacy_required', "Devi accettare la privacy policy"),
        }),

        marketingAccepted: z.boolean().optional(),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<RegisterSchema>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            name: "",
            email: "",
            password: "",
            termsAccepted: false,
            privacyAccepted: false,
            marketingAccepted: false,
        },
        mode: "onChange",
    });

    // Remove watch variables for visual state to prevent race conditions
    // const termsAccepted = watch("termsAccepted");
    // const privacyAccepted = watch("privacyAccepted");

    const onSubmit = (data: RegisterSchema) => {
        onRegister(data);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 md:space-y-5">

            <div className="space-y-1.5 animate-in fade-in slide-in-from-bottom-2">
                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                        {t('auth.register.full_name', 'Nome Completo')}
                    </label>
                    <div className="relative group">
                        <User className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.name ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"}`} size={18} />
                        <input
                            type="text"
                            {...register("name")}
                            className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                                ${errors.name ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"}
                            `}
                            placeholder={t('auth.placeholders.name', 'Mario Rossi')}
                        />
                    </div>
                    {errors.name && (
                        <p className="text-red-500 text-xs font-medium ml-1">{errors.name.message}</p>
                    )}
                </div>
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                    {t('login.email')}
                </label>
                <div className="relative group">
                    <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.email ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"}`} size={18} />
                    <input
                        type="email"
                        {...register("email")}
                        className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                            ${errors.email ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"}
                        `}
                        placeholder={t('auth.placeholders.email')}
                    />
                </div>
                {errors.email && (
                    <p className="text-red-500 text-xs font-medium ml-1">{errors.email.message}</p>
                )}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                    {t('login.password')}
                </label>
                <div className="relative group">
                    <Lock className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.password ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"}`} size={18} />
                    <input
                        type={showPassword ? "text" : "password"}
                        {...register("password")}
                        className={`w-full pl-11 pr-12 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                            ${errors.password ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200" : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"}
                        `}
                        placeholder="••••••••"
                    />
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none p-1 rounded-md transition-colors hover:bg-gray-100"
                        tabIndex={-1}
                    >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>
                {errors.password && (
                    <p className="text-red-500 text-xs font-medium ml-1">{errors.password.message}</p>
                )}
            </div>

            <div className="space-y-3 pt-2">
                <label className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-gray-50 rounded-lg transition-colors -mx-2">
                    <div className="relative flex items-center mt-0.5">
                        <input type="checkbox" className="peer sr-only" {...register("termsAccepted")} />
                        <div className={`w-5 h-5 border-2 rounded-md transition-all flex items-center justify-center bg-white group-hover:border-clip-300 peer-checked:bg-clip-500 peer-checked:border-clip-500 peer-checked:[&>svg]:opacity-100 border-gray-300 ${errors.termsAccepted ? "border-red-300" : ""}`}>
                            <Check size={14} className="text-white opacity-0 transition-opacity" />
                        </div>
                    </div>
                    <span className="text-xs text-gray-500 leading-tight select-none pt-0.5">
                        {t('auth.register.terms_prefix')} <span className="font-bold text-gray-800 hover:underline">{t('auth.register.terms_link')}</span>.*
                    </span>
                </label>
                {errors.termsAccepted && <p className="text-red-500 text-xs font-medium ml-1">{errors.termsAccepted.message}</p>}

                <label className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-gray-50 rounded-lg transition-colors -mx-2">
                    <div className="relative flex items-center mt-0.5">
                        <input type="checkbox" className="peer sr-only" {...register("privacyAccepted")} />
                        <div className={`w-5 h-5 border-2 rounded-md transition-all flex items-center justify-center bg-white group-hover:border-clip-300 peer-checked:bg-clip-500 peer-checked:border-clip-500 peer-checked:[&>svg]:opacity-100 border-gray-300 ${errors.privacyAccepted ? "border-red-300" : ""}`}>
                            <Check size={14} className="text-white opacity-0 transition-opacity" />
                        </div>
                    </div>
                    <span className="text-xs text-gray-500 leading-tight select-none pt-0.5">
                        {t('auth.register.privacy_prefix')} <span className="font-bold text-gray-800 hover:underline">{t('auth.register.privacy_link')}</span>.*
                    </span>
                </label>
                {errors.privacyAccepted && <p className="text-red-500 text-xs font-medium ml-1">{errors.privacyAccepted.message}</p>}

                <label className="flex items-center gap-3 cursor-pointer group p-2 hover:bg-gray-50 rounded-lg transition-colors -mx-2">
                    <div className="relative flex items-center mt-0.5">
                        <input type="checkbox" className="peer sr-only" {...register("marketingAccepted")} />
                        <div className={`w-5 h-5 border-2 rounded-md transition-all flex items-center justify-center bg-white group-hover:border-clip-300 peer-checked:bg-clip-500 peer-checked:border-clip-500 peer-checked:[&>svg]:opacity-100 border-gray-300`}>
                            <Check size={14} className="text-white opacity-0 transition-opacity" />
                        </div>
                    </div>
                    <span className="text-xs text-gray-500 leading-tight select-none pt-0.5">
                        {t('auth.register.marketing_consent', 'Acconsento al trattamento dei dati per finalità di marketing')}
                    </span>
                </label>
            </div>

            <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 mt-2
                    ${loading ? "bg-gray-300 cursor-not-allowed shadow-none" : "bg-gray-900 hover:bg-black shadow-gray-200/50 hover:shadow-gray-300/50"}
                `}
            >
                {loading ? <Loader2 size={20} className="animate-spin" /> : <>{t('auth.register.submit_btn')} <ArrowRight size={18} /></>}
            </button>
        </form>
    );
};

export default AuthRegister;